import React, { useState, useEffect, useMemo, useCallback, useRef, useLayoutEffect } from 'react';
import { RotateCcw } from 'lucide-react';
import { resolveMediaUrl } from '../../../utils/mediaUrl';
import { useTranslation } from '../../../i18n';
import useEditorAutoGrow from './useEditorAutoGrow';

const getMediaUrl = (src) => resolveMediaUrl(src) || '';

const shuffleWithSeed = (array, seed) => {
  if (array.length <= 1) return [...array];
  const seededRandom = (s) => {
    const x = Math.sin(s) * 10000;
    return x - Math.floor(x);
  };
  // Retry shuffle until we get a derangement (no element stays in its original
  // row), so the correct match is never directly opposite its pair.
  for (let attempt = 0; attempt < 20; attempt++) {
    const result = [...array];
    let seedValue = seed + attempt * 1000;
    for (let i = result.length - 1; i > 0; i--) {
      seedValue++;
      const j = Math.floor(seededRandom(seedValue) * (i + 1));
      [result[i], result[j]] = [result[j], result[i]];
    }
    if (result.every((v, i) => v !== array[i])) return result;
  }
  // Fallback: a single rotation is always a derangement for length >= 2.
  return [...array.slice(1), array[0]];
};

const MatchLinesElement = ({
  element,
  isEditor = false,
  studentView = false,
  connections: externalConnections = null,
  showResult = false,
  perPairResults = null,
  onConnectionsChange,
  onCheck,
  onRetry,
  disabled = false,
  hideCheckButton = false,
  hideActionBar = false,
  slideScale = 1,
  onAutoGrow,
}) => {
  const { t } = useTranslation();
  const { content, style, id } = element;
  const {
    pairs = [],
    allowRetry = false,
    lineColor = '#2A4D25',
    dotColor = '#2A4D25',
    itemBgColor = '#ffffff',
    itemTextColor = '#1f2937',
    fontSize = 18,
    fontFamily = 'Inter',
    leftColWidth = 1,
    rightColWidth = 1,
    layout = 'horizontal',
  } = content || {};
  // 'vertical' = top row matches to bottom row (dots under/over the items).
  const isVertical = layout === 'vertical';
  const leftBgColor = content?.leftBgColor ?? itemBgColor;
  const rightBgColor = content?.rightBgColor ?? itemBgColor;
  const leftTextColor = content?.leftTextColor ?? itemTextColor;
  const rightTextColor = content?.rightTextColor ?? itemTextColor;
  const backgroundColor = style?.backgroundColor || '#ffffff';
  // Scale interior pixel sizes with the classroom board scale so the exercise
  // stays proportional on mobile (same relative layout as desktop) rather than
  // rendering 18px fonts and an 80px middle column inside a ~300px board.
  const s = typeof slideScale === 'number' && slideScale > 0 ? slideScale : 1;
  const scaledFontSize = Math.max(8, fontSize * s);
  const scaledDot = Math.max(8, Math.round(18 * s));
  const scaledMidCol = Math.max(24, Math.round(80 * s));
  const scaledPad = Math.max(6, Math.round(16 * s));
  const scaledGapY = Math.max(2, Math.round(12 * s));
  const scaledStroke = Math.max(1.5, 3 * s);
  const scaledImgMaxH = Math.max(32, Math.round(80 * s));
  const scaledItemPadX = Math.max(4, Math.round(12 * s));
  const scaledItemPadY = Math.max(2, Math.round(8 * s));
  const scaledItemGap = Math.max(4, Math.round(12 * s));

  // Right-column items are shuffled with a stable seed (derived from element id)
  // so the order is the same across renders and for every student. We also
  // shuffle in the editor preview so the teacher sees what the student will
  // see — otherwise it looks like every correct answer sits opposite its pair.
  const rightOrder = useMemo(() => {
    if (!pairs || pairs.length === 0) return [];
    const ids = pairs.map((p) => p.id);
    const seed = id ? id.split('').reduce((a, c) => a + c.charCodeAt(0), 0) : 1;
    return shuffleWithSeed(ids, seed);
  }, [pairs, id]);

  // connections: { [pairId]: rightPairId | null }
  const [connections, setConnections] = useState(externalConnections || {});
  useEffect(() => {
    if (externalConnections) setConnections(externalConnections);
  }, [externalConnections]);

  // selected: { side: 'left' | 'right', id } | null
  const [selected, setSelected] = useState(null);

  const updateConnections = useCallback((next) => {
    setConnections(next);
    onConnectionsChange?.(next);
  }, [onConnectionsChange]);

  const interactive = !isEditor && !showResult && !disabled;

  const createConnection = (leftId, rightId) => {
    const next = { ...connections };
    // Remove any other connection that points to this right
    Object.keys(next).forEach((lid) => {
      if (next[lid] === rightId) next[lid] = null;
    });
    next[leftId] = rightId;
    updateConnections(next);
    setSelected(null);
  };

  const handleDotClick = (pairId, side) => {
    if (!interactive) return;
    const isLeft = side === 'left';
    const isConnected = isLeft
      ? !!connections[pairId]
      : Object.values(connections).includes(pairId);

    // Clicking a connected dot removes its connection
    if (isConnected) {
      const next = { ...connections };
      if (isLeft) {
        next[pairId] = null;
      } else {
        const lid = Object.keys(connections).find((k) => connections[k] === pairId);
        if (lid) next[lid] = null;
      }
      updateConnections(next);
      setSelected(null);
      return;
    }

    // Toggle off if clicking the same dot
    if (selected && selected.side === side && selected.id === pairId) {
      setSelected(null);
      return;
    }

    // If a dot on the opposite side is already selected, create the connection
    if (selected && selected.side !== side) {
      const leftId = isLeft ? pairId : selected.id;
      const rightId = isLeft ? selected.id : pairId;
      createConnection(leftId, rightId);
      return;
    }

    // Otherwise (nothing selected, or same side selected): set as the new selection
    setSelected({ side, id: pairId });
  };

  // Dot refs for line geometry
  const containerRef = useRef(null);
  const scrollRef = useRef(null);
  const leftDotRefs = useRef({});
  const rightDotRefs = useRef({});
  const [dotPositions, setDotPositions] = useState({ left: {}, right: {} });

  useEditorAutoGrow({
    enabled: isEditor,
    scrollRef,
    currentHeightPct: element.size?.height || 0,
    contentCount: pairs.length,
    onAutoGrow,
  });

  useLayoutEffect(() => {
    const measure = () => {
      if (!containerRef.current) return;
      const containerRect = containerRef.current.getBoundingClientRect();
      // The slide is rendered inside a `transform: scale(...)` subtree in the
      // classroom (especially on mobile where the slide is scaled to fit).
      // getBoundingClientRect returns post-scale viewport pixels, but the SVG's
      // internal coordinate system is in unscaled CSS pixels, so we normalize
      // relative positions by the container's effective scale.
      const offW = containerRef.current.offsetWidth || containerRect.width || 1;
      const offH = containerRef.current.offsetHeight || containerRect.height || 1;
      const scaleX = containerRect.width / offW || 1;
      const scaleY = containerRect.height / offH || 1;
      const left = {};
      const right = {};
      pairs.forEach((p) => {
        const ldot = leftDotRefs.current[p.id];
        if (ldot) {
          const r = ldot.getBoundingClientRect();
          left[p.id] = {
            x: (r.left + r.width / 2 - containerRect.left) / scaleX,
            y: (r.top + r.height / 2 - containerRect.top) / scaleY,
          };
        }
        const rdot = rightDotRefs.current[p.id];
        if (rdot) {
          const r = rdot.getBoundingClientRect();
          right[p.id] = {
            x: (r.left + r.width / 2 - containerRect.left) / scaleX,
            y: (r.top + r.height / 2 - containerRect.top) / scaleY,
          };
        }
      });
      setDotPositions((prev) => {
        // Avoid unnecessary state updates if positions are unchanged — prevents
        // measure → setState → re-render → measure loops in some layouts.
        const same = (a, b) => {
          const ak = Object.keys(a);
          const bk = Object.keys(b);
          if (ak.length !== bk.length) return false;
          for (const k of ak) {
            if (!b[k]) return false;
            if (a[k].x !== b[k].x || a[k].y !== b[k].y) return false;
          }
          return true;
        };
        if (same(left, prev.left) && same(right, prev.right)) return prev;
        return { left, right };
      });
    };
    measure();
    if (typeof ResizeObserver !== 'undefined' && containerRef.current) {
      const ro = new ResizeObserver(measure);
      ro.observe(containerRef.current);
      return () => ro.disconnect();
    }
  }, [pairs, rightOrder, fontSize, leftColWidth, rightColWidth, s, isVertical]);

  const allConnected = pairs.length > 0 && pairs.every((p) => connections[p.id]);
  const allCorrect = showResult && perPairResults && pairs.length > 0
    && pairs.every((p) => perPairResults[p.id] === true);

  const handleCheck = () => onCheck?.(connections);
  const handleRetry = () => {
    setConnections({});
    setSelected(null);
    onRetry?.();
  };

  const renderItem = (pair, side) => {
    const c = side === 'left' ? pair.left : pair.right;
    const hasAudio = !!(c && c.audioUrl);
    const hasImage = !!(c && c.imageUrl);
    const text = c?.text || '';
    return (
      <div
        className="flex items-center h-full"
        style={{
          flexDirection: isVertical
            ? (side === 'left' ? 'column' : 'column-reverse')
            : (side === 'left' ? 'row' : 'row-reverse'),
          gap: `${scaledItemGap}px`,
        }}
      >
        <div
          className={`flex-1 min-w-0 rounded-lg border flex items-center justify-center text-center ${isVertical ? 'w-full' : ''}`}
          style={{
            backgroundColor: side === 'left' ? leftBgColor : rightBgColor,
            color: side === 'left' ? leftTextColor : rightTextColor,
            borderColor: '#e5e7eb',
            fontSize: `${scaledFontSize}px`,
            fontFamily,
            padding: `${scaledItemPadY}px ${scaledItemPadX}px`,
            minHeight: (hasImage || hasAudio) ? undefined : `${scaledFontSize + 18 * s}px`,
          }}
        >
          {hasAudio ? (
            <audio
              src={getMediaUrl(c.audioUrl)}
              controls
              controlsList="nodownload noplaybackrate noremoteplayback"
              preload="metadata"
              className="w-full match-lines-audio"
              style={{ maxWidth: '100%', height: `${Math.max(24, Math.round(32 * s))}px` }}
              onClick={(e) => e.stopPropagation()}
            />
          ) : hasImage ? (
            <img
              src={getMediaUrl(c.imageUrl)}
              alt={text}
              style={{ maxHeight: `${scaledImgMaxH}px` }}
              className="object-contain"
              draggable={false}
            />
          ) : (
            <span className="break-words">{text || <span className="text-gray-300">—</span>}</span>
          )}
        </div>
        {renderDot(pair.id, side)}
      </div>
    );
  };

  const renderDot = (pairId, side) => {
    const isLeft = side === 'left';
    const isConnected = isLeft
      ? !!connections[pairId]
      : Object.values(connections).includes(pairId);
    const isSelected = selected && selected.side === side && selected.id === pairId;
    const pairResult = showResult && perPairResults
      ? (isLeft ? perPairResults[pairId] : perPairResults[
          Object.keys(connections).find((lid) => connections[lid] === pairId)
        ])
      : null;
    let bg = isConnected ? dotColor : '#ffffff';
    let border = dotColor;
    if (pairResult === true) { bg = '#22c55e'; border = '#16a34a'; }
    else if (pairResult === false) { bg = '#ef4444'; border = '#dc2626'; }
    if (isSelected) { border = '#6366f1'; }

    return (
      <button
        ref={(el) => {
          if (isLeft) leftDotRefs.current[pairId] = el;
          else rightDotRefs.current[pairId] = el;
        }}
        type="button"
        onClick={() => handleDotClick(pairId, side)}
        disabled={!interactive}
        className={`flex-shrink-0 rounded-full transition-all ${interactive ? 'cursor-pointer hover:scale-110' : 'cursor-default'}`}
        style={{
          width: scaledDot,
          height: scaledDot,
          backgroundColor: bg,
          border: `${Math.max(1.5, 3 * s)}px solid ${border}`,
          boxShadow: isSelected ? `0 0 0 ${Math.max(1.5, 3 * s)}px rgba(99, 102, 241, 0.25)` : undefined,
        }}
        aria-label={isLeft ? t('games.matchLines.dotLeft') : t('games.matchLines.dotRight')}
      />
    );
  };

  const hideChrome = !isEditor;

  return (
    <div
      className={`w-full h-full rounded-xl overflow-hidden relative flex flex-col ${
        hideChrome ? '' : 'border-2 shadow-sm'
      }`}
      style={{
        backgroundColor: hideChrome ? 'transparent' : backgroundColor,
        borderColor: '#e5e7eb',
      }}
    >
      {pairs.length === 0 ? (
        <div className="flex-1 flex items-center justify-center text-sm text-gray-400 p-4 text-center">
          {t('games.matchLines.addPairsInSettings')}
        </div>
      ) : (
        <div ref={scrollRef} className="flex-1 relative overflow-auto" style={{ padding: `${scaledPad}px` }}>
          {/* Inner wrapper shares one coordinate frame for the grid AND the SVG
              overlay, so they stay aligned when the outer container scrolls
              (mobile: tall wrapped text can force vertical scroll). Ref points
              here so measurements are relative to this frame, not the viewport
              of the scrolling parent. */}
          <div ref={containerRef} className="relative">
          <div
            className="grid items-stretch relative"
            style={isVertical ? {
              gridTemplateColumns: `repeat(${pairs.length}, minmax(0, 1fr))`,
              gridTemplateRows: `auto ${scaledMidCol}px auto`,
              columnGap: `${scaledGapY}px`,
            } : {
              gridTemplateColumns: `${Math.max(0.1, Number(leftColWidth) || 1)}fr ${scaledMidCol}px ${Math.max(0.1, Number(rightColWidth) || 1)}fr`,
              gridAutoRows: 'minmax(0, 1fr)',
              rowGap: `${scaledGapY}px`,
            }}
          >
            {pairs.map((p, idx) => (
              <div
                key={`left_${p.id}`}
                style={isVertical ? { gridColumn: idx + 1, gridRow: 1 } : { gridColumn: 1, gridRow: idx + 1 }}
              >
                {renderItem(p, 'left')}
              </div>
            ))}
            {rightOrder.map((pid, idx) => {
              const pair = pairs.find((p) => p.id === pid);
              if (!pair) return null;
              return (
                <div
                  key={`right_${pid}`}
                  style={isVertical ? { gridColumn: idx + 1, gridRow: 3 } : { gridColumn: 3, gridRow: idx + 1 }}
                >
                  {renderItem(pair, 'right')}
                </div>
              );
            })}
          </div>

          {/* SVG overlay for connection lines */}
          <svg
            className="absolute inset-0 pointer-events-none"
            style={{ width: '100%', height: '100%' }}
          >
            {pairs.map((p) => {
              const rightId = connections[p.id];
              if (!rightId) return null;
              const a = dotPositions.left[p.id];
              const b = dotPositions.right[rightId];
              if (!a || !b) return null;
              let stroke = lineColor;
              if (showResult && perPairResults) {
                stroke = perPairResults[p.id] === true ? '#22c55e' : '#ef4444';
              }
              return (
                <line
                  key={`line_${p.id}`}
                  x1={a.x}
                  y1={a.y}
                  x2={b.x}
                  y2={b.y}
                  stroke={stroke}
                  strokeWidth={scaledStroke}
                  strokeLinecap="round"
                />
              );
            })}
          </svg>
          </div>
        </div>
      )}

      {/* Action bar (only in student/preview, not in editor) */}
      {!isEditor && !hideActionBar && pairs.length > 0 && (
        <div className="absolute bottom-2 right-2 z-10 flex items-center gap-2">
          {showResult && allowRetry && !allCorrect && (
            <button
              onClick={handleRetry}
              className="flex items-center gap-1.5 px-3 py-1.5 text-sm font-medium text-gray-700 bg-white border border-gray-200 hover:bg-gray-50 rounded-lg shadow-sm transition"
            >
              <RotateCcw size={14} />
              {t('games.common.tryAgain')}
            </button>
          )}
          {!showResult && !hideCheckButton && (
            <button
              onClick={handleCheck}
              disabled={!allConnected}
              className={`px-4 py-1.5 text-sm font-medium rounded-lg shadow-sm transition ${
                allConnected
                  ? 'bg-indigo-500 text-white hover:bg-indigo-600'
                  : 'bg-gray-200 text-gray-400 cursor-not-allowed'
              }`}
            >
              {t('games.common.check')}
            </button>
          )}
        </div>
      )}

    </div>
  );
};

export default MatchLinesElement;
